import { type ReactNode, useId } from 'react';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';

import { chipSx, wf } from '../shell/tokens';

import { IntakeJourneyStatus } from './IntakeJourneyStatus';
import type { IntakeServiceTier, IntakeSetupService } from './IntakeSetupServicesPage';

export const INTAKE_STATUS_TABS = ['billing', 'credentialing', 'ehr-import'] as const;
export type IntakeStatusTab = (typeof INTAKE_STATUS_TABS)[number];

export const INTAKE_STATUS_TAB_LABELS: Record<IntakeStatusTab, string> = {
  billing: 'Billing',
  credentialing: 'Credentialing',
  'ehr-import': 'EHR Migration',
};

/**
 * The services saved in Agreements & setup decide which tracks appear. Billing follows JotBill
 * or the Self-Pay tier, Credentialing follows JotCred, and EHR Migration has no election so it
 * always shows. Before anything is saved every track stays visible.
 */
export function intakeStatusTabsForServices(
  tier: IntakeServiceTier,
  services: readonly IntakeSetupService[],
): IntakeStatusTab[] {
  const billing = tier === 'self_pay' || services.includes('jotbill');
  const credentialing = services.includes('jotcred');
  if (!billing && !credentialing) return [...INTAKE_STATUS_TABS];
  return INTAKE_STATUS_TABS.filter(
    tab =>
      tab === 'ehr-import' ||
      (tab === 'billing' && billing) ||
      (tab === 'credentialing' && credentialing),
  );
}

/** Keep a stale or hand-typed selection from pointing at a track the practice did not elect. */
export function effectiveIntakeStatusTab(
  requested: IntakeStatusTab,
  available: readonly IntakeStatusTab[],
): IntakeStatusTab {
  return available.includes(requested) ? requested : (available[0] ?? 'billing');
}

export function CredentialingComingSoon() {
  return (
    <IntakeJourneyStatus title="Credentialing" statusLabel="Coming soon">
      <Typography>Your practice’s credentialing progress will appear here.</Typography>
    </IntakeJourneyStatus>
  );
}

export function EhrImportComingSoon({ action }: { action?: ReactNode }) {
  return (
    <IntakeJourneyStatus title="Import patients" statusLabel="Coming soon" action={action}>
      <Typography>
        Independent of verification and payers. Start it whenever your export is ready. Your first
        claim needs a patient with insurance on file, so this and payer connections both have to
        finish before you can bill.
      </Typography>
    </IntakeJourneyStatus>
  );
}

/** The status shell composes the existing service surfaces without inventing completion state. */
export function IntakeStatusDashboard({
  selectedTab,
  onSelectTab,
  billing,
  ehrImport,
  credentialing,
  credentialingAvailable = Boolean(credentialing),
  billingStatus = 'View current status',
  credentialingLockedReason,
  keepPanelsMounted = false,
  availableTabs = INTAKE_STATUS_TABS,
}: {
  keepPanelsMounted?: boolean;
  selectedTab: IntakeStatusTab;
  onSelectTab: (tab: IntakeStatusTab) => void;
  billing: ReactNode;
  ehrImport?: ReactNode;
  credentialing?: ReactNode;
  /** Availability is independent of whether the active panel is mounted. */
  credentialingAvailable?: boolean;
  billingStatus?: string;
  credentialingLockedReason?: string;
  /** Tracks the practice elected; see intakeStatusTabsForServices. Defaults to every track. */
  availableTabs?: readonly IntakeStatusTab[];
}) {
  const id = useId();
  const tabs = [
    {
      value: 'billing',
      label: INTAKE_STATUS_TAB_LABELS.billing,
      caption: 'Your payer connections',
      status: billingStatus,
    },
    {
      value: 'credentialing',
      label: INTAKE_STATUS_TAB_LABELS.credentialing,
      caption: credentialingLockedReason ?? 'Credentialing for your practice',
      status: credentialingLockedReason
        ? 'Locked'
        : credentialingAvailable
          ? 'View current status'
          : 'Coming soon',
    },
    {
      value: 'ehr-import',
      label: INTAKE_STATUS_TAB_LABELS['ehr-import'],
      caption: 'Bring your patient records',
      status: 'Coming soon',
    },
  ].filter(tab => availableTabs.includes(tab.value as IntakeStatusTab));
  const activeTab = effectiveIntakeStatusTab(selectedTab, availableTabs);
  return (
    <Stack spacing={3}>
      <Tabs
        value={activeTab}
        onChange={(_, value: IntakeStatusTab) => onSelectTab(value)}
        aria-label="Intake Status services"
        variant="fullWidth"
        sx={{
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-list': { gap: 1.5 },
          '& .MuiTab-root': { minWidth: 0 },
        }}
      >
        {tabs.map(tab => (
          <Tab
            key={tab.value}
            value={tab.value}
            id={`${id}-${tab.value}-tab`}
            aria-controls={`${id}-${tab.value}-panel`}
            sx={{
              border: '2px solid',
              borderColor: activeTab === tab.value ? wf.blue : wf.line,
              borderRadius: 2,
              bgcolor: wf.surface,
              textAlign: 'left',
              textTransform: 'none',
              alignItems: 'stretch',
              p: 2,
            }}
            label={
              <Stack spacing={1}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                  }}
                >
                  <Typography component="span" sx={{ fontWeight: 700, color: wf.ink }}>
                    {tab.label}
                  </Typography>
                  <Box component="span" sx={chipSx('mut')}>
                    {tab.status}
                  </Box>
                </Stack>
                <Typography component="span" variant="body2" sx={{ color: wf.muted }}>
                  {tab.caption}
                </Typography>
              </Stack>
            }
          />
        ))}
      </Tabs>
      {tabs
        .filter(tab => keepPanelsMounted || tab.value === activeTab)
        .map(tab => (
          <Box
            key={tab.value}
            role="tabpanel"
            hidden={tab.value !== activeTab}
            inert={tab.value !== activeTab}
            id={`${id}-${tab.value}-panel`}
            aria-labelledby={`${id}-${tab.value}-tab`}
          >
            {tab.value === 'billing' ? (
              billing
            ) : tab.value === 'credentialing' ? (
              credentialingLockedReason ? (
                <IntakeJourneyStatus title="Credentialing" statusLabel="Locked">
                  <Typography>{credentialingLockedReason}</Typography>
                </IntakeJourneyStatus>
              ) : (
                (credentialing ?? <CredentialingComingSoon />)
              )
            ) : (
              (ehrImport ?? <EhrImportComingSoon />)
            )}
          </Box>
        ))}
    </Stack>
  );
}
